import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { crmToast, CrmToaster, SearchInput } from './crmUi'
import { getAntiChurn, AcCustomer, AcPayload } from './crmApi'

/**
 * ANTI-CHURN DASH (Lee, 23 Oct 2026) — replaces the Customer Success page.
 * Built to the sandbox spec (handoff/anti-churn-dash): fixed KPI strip,
 * fixed filter row, ONE scrolling list with sticky headers, fixed pagination.
 * Row click opens the churn SIDE PANEL (Lee's call) with a Go-to-lead button.
 * Status thresholds + price book live on the API side.
 */

const PAGE_SIZE = 50 // was 10 — Lee, 27 Oct 2026: 50 per page, table scrolls
const STATUS_COLOR: Record<string, string> = { 'At risk': '#b91c1c', Cooling: '#b45309', Healthy: '#047857', New: '#2563eb', 'No data': '#64748b', 'Stale feed': '#92400e' }
const STATUS_RANK: Record<string, number> = { 'At risk': 0, Cooling: 1, 'Stale feed': 2, 'No data': 3, New: 4, Healthy: 5 }
const gbp = (n: number) => '£' + Math.round(n).toLocaleString('en-GB')
const tenure = (n: number) => (n < 60 ? `${n}d` : n < 730 ? `${Math.round(n / 30.4)} mo` : `${(n / 365).toFixed(1)} yr`)
const dmy = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

const StatusPill: React.FC<{ st: string }> = ({ st }) => {
  const c = STATUS_COLOR[st] || '#64748b'
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap border"
      style={{ color: c, background: `color-mix(in srgb, ${c} 9%, white)`, borderColor: `color-mix(in srgb, ${c} 28%, white)` }}>
      {st.toUpperCase()}
    </span>
  )
}

type SortKey = 'risk' | 'gap' | 'mrr' | 'quiet' | 'paid'
const SORTS: Array<[SortKey, string]> = [['risk', 'Risk first'], ['gap', 'Biggest gap'], ['mrr', 'Highest MRR'], ['quiet', 'Longest since quote'], ['paid', 'Newest paid']]
const TABS = ['All', 'At risk', 'Cooling', 'Healthy', 'New'] as const

export const CrmAntiChurn: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<AcPayload | null>(null)
  const [tab, setTab] = useState<string>('All')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('risk')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<AcCustomer | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getAntiChurn().then(setData).catch(() => crmToast('Could not load the Anti-Churn Dash', 'error'))
    const t = setInterval(() => getAntiChurn().then(setData).catch(() => {}), 120_000)
    return () => clearInterval(t)
  }, [])
  useEffect(() => { setPage(1); if (listRef.current) listRef.current.scrollTop = 0 }, [tab, q, sort])

  const all = data?.customers || []
  const counts = useMemo(() => {
    const m: Record<string, number> = { All: all.length }
    for (const c of all) m[c.status] = (m[c.status] || 0) + 1
    return m
  }, [all])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = all.filter((c) => (tab === 'All' || c.status === tab) && (!needle || c.company.toLowerCase().includes(needle)))
    const cmp: Record<SortKey, (a: AcCustomer, b: AcCustomer) => number> = {
      risk: (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.mrr - a.mrr,
      gap: (a, b) => b.gap - a.gap,
      mrr: (a, b) => b.mrr - a.mrr,
      quiet: (a, b) => (b.noData ? -1 : (b.daysSinceTouch ?? b.daysSinceQuote) ?? -1) - (a.noData ? -1 : (a.daysSinceTouch ?? a.daysSinceQuote) ?? -1),
      paid: (a, b) => a.paidDays - b.paidDays,
    }
    return [...list].sort(cmp[sort])
  }, [all, tab, q, sort])

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pg = Math.min(page, pages)
  const slice = filtered.slice((pg - 1) * PAGE_SIZE, pg * PAGE_SIZE)
  const k = data?.kpis

  const kpiCards = k ? [
    { label: 'Paid customers', v: String(k.paidCustomers), sub: 'live accounts', c: '#0f172a' },
    { label: 'Current MRR', v: gbp(k.mrr), sub: `${k.capturedPct}% of potential`, c: '#0f172a' },
    { label: 'Potential MRR', v: gbp(k.potential), sub: 'if every module sold', c: '#0f172a' },
    { label: 'Untapped MRR', v: gbp(k.untapped), sub: 'left to sell', c: '#2563eb' },
    { label: 'MRR at risk', v: gbp(k.mrrAtRisk), sub: `${k.atRiskCount} accounts at risk`, c: '#b91c1c' },
  ] : []

  const setPg = (p: number) => { setPage(p); if (listRef.current) listRef.current.scrollTop = 0 }

  const th = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-txt-tertiary whitespace-nowrap bg-surface sticky top-0 z-10 border-b border-border'
  return (
    <div className="h-full flex flex-col bg-surface-secondary overflow-auto p-4 gap-3" style={{ minHeight: 0 }}>
      <CrmToaster />
      <div className="shrink-0">
        <h1 className="text-[20px] font-bold text-txt-primary">Anti-Churn Dash</h1>
        <p className="text-[12px] text-txt-tertiary">Every paid customer — who’s going quiet, and what’s still left to sell them.</p>
      </div>

      {/* KPI strip */}
      <div className="shrink-0 grid grid-cols-2 md:grid-cols-5 gap-2.5">
        {kpiCards.map((c) => (
          <div key={c.label} className="rounded-xl bg-surface border border-border px-3.5 py-2.5 min-w-0">
            <p className="text-[11px] text-txt-tertiary">{c.label}</p>
            <p className="text-[19px] font-bold truncate tabular-nums" style={{ color: c.c }}>{c.v}</p>
            <p className="text-[11px] text-txt-tertiary truncate">{c.sub}</p>
          </div>
        ))}
        {!k && [0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-[74px]" />)}
      </div>

      {/* Tabs · search · sort */}
      <div className="shrink-0 flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${tab === t ? 'bg-txt-primary text-surface border-txt-primary' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}>
            {t} <span className="opacity-60">({counts[t] ?? 0})</span>
          </button>
        ))}
        <span className="flex-1" />
        <SearchInput value={q} onChange={setQ} placeholder="Search companies…" />
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-lg border border-border bg-surface px-2 py-1.5 text-[11px] text-txt-secondary focus:outline-none">
          {SORTS.map(([kk, label]) => <option key={kk} value={kk}>{label}</option>)}
        </select>
      </div>

      {/* The ONLY scrolling zone */}
      <div ref={listRef} className="flex-1 rounded-xl bg-surface border border-border overflow-auto" style={{ minHeight: 240 }}>
        <table className="w-full border-collapse" style={{ minWidth: 1100 }}>
          <thead>
            <tr>
              <th className={th}>Company</th>
              <th className={th}>Status</th>
              <th className={th}>Paying</th>
              <th className={th}>Last quote</th>
              <th className={`${th} text-right`}>Quotes 14d</th>
              <th className={`${th} text-right`}>MRR</th>
              <th className={`${th} text-right`}>Potential</th>
              <th className={th}>Gap to potential</th>
              <th className={th}>Modules paid</th>
              <th className={th}>Left to sell</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((c) => {
              const lastQ = c.noData ? null : c.daysSinceQuote
              // Risk colouring follows last TOUCH (modified beats created) when
              // the feed sends it (Lee, 27 Oct 2026) — created stays visible.
              const quiet = c.noData ? null : (c.daysSinceTouch ?? c.daysSinceQuote)
              const touched = !c.noData && c.daysSinceTouch != null && c.daysSinceTouch !== c.daysSinceQuote
              const qColor = quiet == null ? 'text-txt-tertiary' : quiet >= 14 ? 'text-red-700 font-bold' : quiet >= 5 ? 'text-amber-700 font-bold' : 'text-txt-primary'
              return (
                <tr key={c.id} onClick={() => setOpen(c)} className="cursor-pointer hover:bg-surface-hover/50 border-b border-border/60">
                  <td className="px-3 py-2.5 min-w-0">
                    <p className="text-[13px] font-bold text-txt-primary truncate max-w-[220px]">{c.company}</p>
                    <p className="text-[10px] text-txt-tertiary">{c.plan} plan{c.exactQuoteData ? '' : ' · approx. activity'}</p>
                  </td>
                  <td className="px-3 py-2.5"><StatusPill st={c.status} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap" title={`Source: ${c.billing.source} · paid since ${dmy(c.paidAt)}`}>
                    <p className="text-[12px] font-semibold text-txt-primary">{c.billing.label}</p>
                    <p className="text-[10px] text-txt-tertiary">customer {tenure(c.paidDays)}</p>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <p className={`text-[12px] ${qColor}`} title={lastQ == null && !c.noData ? 'No quotes anywhere in the usage history WeQuote has sent us (~5 weeks)' : undefined}>{c.noData ? '—' : lastQ === 0 ? 'This week' : lastQ == null ? 'None on record' : `~${lastQ}d ago`}</p>
                    <p className="text-[10px] text-txt-tertiary">{c.noData ? 'No quote data' : touched ? `touched ${c.daysSinceTouch === 0 ? 'this week' : `${c.daysSinceTouch}d ago`}` : c.lastQuoteAt ? dmy(c.lastQuoteAt) : ''}</p>
                  </td>
                  <td className={`px-3 py-2.5 text-right text-[12px] tabular-nums ${!c.noData && c.quotes14 === 0 ? 'text-red-700 font-bold' : 'text-txt-primary'}`}>{c.noData ? '—' : c.quotes14}</td>
                  <td className="px-3 py-2.5 text-right text-[12px] font-semibold text-txt-primary tabular-nums">{gbp(c.mrr)}</td>
                  <td className="px-3 py-2.5 text-right text-[12px] text-txt-secondary tabular-nums">{gbp(c.potential)}</td>
                  <td className="px-3 py-2.5 min-w-[130px]">
                    {c.gap > 0 ? (
                      <>
                        <p className="text-[11px] font-bold text-blue-700">+{gbp(c.gap)} <span className="font-normal text-txt-tertiary">· {c.captured}% captured</span></p>
                        <div className="h-1.5 rounded-full bg-surface-tertiary mt-1 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${c.captured}%` }} /></div>
                      </>
                    ) : <span className="text-[11px] text-txt-tertiary">—</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="inline-flex gap-0.5 align-middle">
                      {c.owned.map((o, i) => <span key={i} className="w-2.5 h-2.5 rounded-[3px]" title={c.left.length ? undefined : undefined} style={{ background: o ? '#2563eb' : '#e2e8f0' }} />)}
                    </span>
                    <span className="text-[10px] text-txt-tertiary ml-1.5">{c.paidN} of {c.owned.length}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {c.left.length === 0 ? <span className="text-[11px] font-bold text-emerald-700">Fully sold</span> : (
                      <span className="inline-flex gap-1 flex-wrap">
                        {c.left.slice(0, 3).map((m) => (
                          <span key={m.key} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-surface-secondary text-txt-secondary border border-border whitespace-nowrap" title={`${gbp(m.monthly)}/mo${m.perUser ? ' per user' : ''}`}>{m.label.replace(' integration', '')}</span>
                        ))}
                        {c.left.length > 3 && <span className="text-[10px] text-txt-tertiary">+{c.left.length - 3}</span>}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
            {data && slice.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-[12px] text-txt-tertiary">Nothing matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="shrink-0 flex items-center justify-between">
        <span className="text-[11px] text-txt-tertiary">{filtered.length ? `${(pg - 1) * PAGE_SIZE + 1}–${Math.min(filtered.length, pg * PAGE_SIZE)} of ${filtered.length}` : '0 of 0'}</span>
        <span className="inline-flex items-center gap-1">
          <button disabled={pg <= 1} onClick={() => setPg(pg - 1)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-border text-txt-secondary disabled:opacity-40 hover:bg-surface-hover">← Prev</button>
          {Array.from({ length: pages }, (_, i) => i + 1).filter((p) => p === 1 || p === pages || Math.abs(p - pg) <= 1).map((p, i, arr) => (
            <React.Fragment key={p}>
              {i > 0 && arr[i - 1] !== p - 1 && <span className="text-[11px] text-txt-tertiary">…</span>}
              <button onClick={() => setPg(p)} className={`w-7 h-7 rounded-lg text-[11px] font-bold ${p === pg ? 'bg-txt-primary text-surface' : 'text-txt-secondary border border-border hover:bg-surface-hover'}`}>{p}</button>
            </React.Fragment>
          ))}
          <button disabled={pg >= pages} onClick={() => setPg(pg + 1)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-border text-txt-secondary disabled:opacity-40 hover:bg-surface-hover">Next →</button>
        </span>
      </div>

      {/* ── Churn side panel (Lee: detailed info + Go to lead) ── */}
      {open && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute right-0 top-0 bottom-0 w-[420px] max-w-[92vw] bg-surface border-l border-border shadow-2xl p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[16px] font-extrabold text-txt-primary truncate">{open.company}</p>
                <p className="text-[11px] text-txt-tertiary">{open.plan} plan · {open.billing.label} · paid since {dmy(open.paidAt)} ({tenure(open.paidDays)})</p>
              </div>
              <StatusPill st={open.status} />
            </div>

            {/* why this status */}
            <div className="mt-3 rounded-lg bg-surface-secondary/60 px-3 py-2 text-[12px] text-txt-secondary leading-relaxed">
              {open.status === 'Stale feed' ? `WeQuote's usage push stopped covering this account on ${dmy((open as any).feedStaleSince)} (a feed bug on their side — Robert has the brief). Their activity since then is unknown, NOT necessarily quiet.`
                : open.noData ? 'No usage feed for this account — WeQuote has sent no data yet.'
                : open.status === 'New' ? `Paid for ${open.paidDays} days — still onboarding, not judged on activity yet.`
                : open.status === 'At risk' ? `${(open.daysSinceTouch ?? open.daysSinceQuote) ?? '14+'} days since they last touched a quote${open.quotes14 === 0 ? ' and 0 quotes created in the last 14 days' : ''}. Call them.`
                : open.status === 'Cooling' ? `${open.daysSinceTouch ?? open.daysSinceQuote} days since they last touched a quote · ${open.quotes14} quote${open.quotes14 === 1 ? '' : 's'} created in 14 days — slowing down.`
                : 'Quoting regularly — healthy.'}
              {!open.exactQuoteData && !open.noData && <span className="block text-[10px] text-txt-tertiary mt-1">Activity approximated from usage periods — exact when WeQuote’s per-quote fields land.</span>}
            </div>

            {/* money */}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[['MRR', gbp(open.mrr), '#0f172a'], ['Potential', gbp(open.potential), '#0f172a'], ['Gap', open.gap > 0 ? `+${gbp(open.gap)}` : '—', '#2563eb']].map(([l, v, c]) => (
                <div key={l as string} className="rounded-lg border border-border px-2 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">{l}</p>
                  <p className="text-[15px] font-bold tabular-nums" style={{ color: c as string }}>{v}</p>
                </div>
              ))}
            </div>
            {open.gap > 0 && (
              <div className="mt-2">
                <div className="h-2 rounded-full bg-surface-tertiary overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${open.captured}%` }} /></div>
                <p className="text-[10px] text-txt-tertiary mt-0.5">{open.captured}% of potential captured</p>
              </div>
            )}

            {/* modules */}
            <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Modules</p>
            <div className="mt-1.5 grid gap-1.5">
              {open.left.length === 0 && <p className="text-[12px] font-bold text-emerald-700">Fully sold — every module on the book.</p>}
              {open.left.map((m) => (
                <div key={m.key} className="flex items-center justify-between rounded-lg border border-border px-2.5 py-1.5">
                  <span className="text-[12px] text-txt-primary">{m.label}</span>
                  <span className="text-[11px] font-bold text-blue-700">{gbp(m.monthly)}/mo{m.perUser ? ' per user' : ''}</span>
                </div>
              ))}
            </div>

            {/* usage trend */}
            {open.trend.length > 0 && (
              <>
                <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Quotes per period (newest first)</p>
                <div className="mt-1.5 flex items-end gap-1 h-[64px]">
                  {open.trend.map((t, i) => {
                    const max = Math.max(1, ...open.trend.map((x) => x.quotes))
                    return <div key={i} className="flex-1 rounded-t bg-brand-500/80" title={`${dmy(t.start)}–${dmy(t.end)}: ${t.quotes} quotes · ${t.logins} logins`} style={{ height: `${Math.max(6, (t.quotes / max) * 100)}%`, opacity: 1 - i * 0.09 }} />
                  })}
                </div>
              </>
            )}
            {open.lastActiveAt && <p className="mt-2 text-[11px] text-txt-tertiary">Last active in WeQuote: {dmy(open.lastActiveAt)}</p>}

            <button onClick={() => navigate(`/crm/lead/${open.id}`)}
              className="mt-5 w-full py-2.5 rounded-lg text-[13px] font-bold bg-brand-600 text-white hover:bg-brand-700">
              Go to lead →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
